import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getPermisosOt, getUsuariosPermisos, getRolesPermisos, getPortadoresPermisos, createPermisoOt, updatePermisoOt, deletePermisoOt, getOtConfig,
} from '../api/client'
import PageHeader from '../components/PageHeader'
import Asignables from '../components/ot/Asignables'
import AsignarA from '../components/ot/AsignarA'
import PersonasConFuncion from '../components/ot/PersonasConFuncion'
import { formatFecha } from '../components/unidades/tipos'

// Operaciones > Planta > Permisos: who may exercise each function of the OT
// flow -- everyone with a role, or one person by name. This is the whole
// permission model (nothing is tied to a job title in the code): moving who
// admits, plans or closes is done here. Takes "administrar_permisos".
export default function PlantaPermisos() {
  const { t } = useTranslation(['operations', 'common', 'settings'])
  const [funciones, setFunciones] = useState(null)
  const [usuarios, setUsuarios] = useState([])
  const [roles, setRoles] = useState([])
  const [portadores, setPortadores] = useState({ cargos: [], areas: [], personas: [] })
  const [sinPermiso, setSinPermiso] = useState(false)
  const [error, setError] = useState('')
  const [viendo, setViendo] = useState(null)         // the function whose holders are open

  const cargar = useCallback(() => {
    getPermisosOt().then(setFunciones).catch(err => {
      if (err.response?.status === 403) setSinPermiso(true)
      else setError(t('ot.permisos.cargaFallo'))
    })
  }, [t])
  useEffect(() => {
    getOtConfig().then(c => {
      if (!c.funciones.includes('administrar_permisos')) { setSinPermiso(true); return }
      cargar()
      getUsuariosPermisos().then(setUsuarios).catch(() => {})
      getRolesPermisos().then(setRoles).catch(() => {})
      getPortadoresPermisos().then(setPortadores).catch(() => {})
    }).catch(() => setError(t('ot.permisos.cargaFallo')))
  }, [cargar, t])

  const rolNombre = (r) => t(`common:roles.${r}`, { defaultValue: t(`settings:roles.${r}`, { defaultValue: r }) })

  const nombreAsignacion = (a) => {
    if (a.rol) return `${t('ot.permisos.rolHeredado')}: ${rolNombre(a.rol)}`
    if (a.area) {
      const area = portadores.areas.find(x => x.code === a.area)
      return t('ot.permisos.personasDe', { area: area?.name || a.area })
    }
    if (a.cargo) return `${t('ot.permisos.cargo')}: ${a.cargo}`
    if (a.jefe_de_area) {
      const area = portadores.areas.find(x => x.code === a.jefe_de_area)
      return t('ot.permisos.jefeDe', { area: area?.name || a.jefe_de_area })
    }
    if (a.persona_id) return a.persona_nombre || `#${a.persona_id}`
    return a.usuario_nombre || a.usuario_email
  }
  const colorAsignacion = (a) => a.rol ? 'bg-gray-100 text-gray-600 italic'
    : a.area ? 'bg-sky-100 text-sky-900'
    : a.cargo ? 'bg-emerald-100 text-emerald-900'
    : a.jefe_de_area ? 'bg-amber-100 text-amber-900' : 'bg-violet-100 text-violet-900'
  const agregar = async (funcion, valor) => {
    if (!valor) return
    const i = valor.indexOf(':')
    const tipo = valor.slice(0, i)
    const id = valor.slice(i + 1)
    setError('')
    try {
      const quien = {
        rol: { rol: id }, usuario: { usuario_id: Number(id) }, persona: { persona_id: Number(id) },
        cargo: { cargo: id }, jefe: { jefe_de_area: id }, area: { area: id },
      }[tipo]
      await createPermisoOt({ funcion_codigo: funcion, ...quien })
      cargar()
    } catch (err) { setError(err.response?.data?.detail?.message || t('ot.panel.fallo')) }
  }
  const quitar = async (f, a) => {
    const quien = nombreAsignacion(a)
    if (!window.confirm(t('ot.permisos.quitarConfirm', { quien, funcion: t(`ot.funciones.${f.codigo}`, { defaultValue: f.codigo }) }))) return
    setError('')
    try { await deletePermisoOt(a.id); cargar() }
    catch (err) { setError(err.response?.data?.detail?.message || t('ot.panel.fallo')) }
  }
  const revisar = async (a) => {
    const v = window.prompt(t('ot.permisos.revisionPregunta'), a.fecha_revision || '')
    if (v === null) return
    if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) { setError(t('ot.permisos.revisionFormato')); return }
    try { await updatePermisoOt(a.id, { fecha_revision: v || null }); cargar() }
    catch { setError(t('ot.panel.fallo')) }
  }

  if (sinPermiso) return <div className="p-8 text-sm text-gray-500">{t('ot.permisos.sinFuncion')}</div>

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader title={t('ot.permisos.titulo')} subtitle={t('ot.permisos.subtitulo')} />
      </div>
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {!funciones ? <p className="text-sm text-gray-400">{t('common:loading', { defaultValue: 'Cargando…' })}</p> : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-5 py-3 font-semibold text-gray-600 w-[30%]">{t('ot.permisos.funcion')}</th>
                <th className="px-5 py-3 font-semibold text-gray-600">{t('ot.permisos.quien')}</th>
                <th className="px-5 py-3 w-56"></th>
              </tr>
            </thead>
            <tbody>
              {funciones.map(f => {
                const ya = new Set(f.asignaciones.map(a => (
                  a.rol ? `rol:${a.rol}` : a.area ? `area:${a.area}` : a.cargo ? `cargo:${a.cargo}` : a.jefe_de_area ? `jefe:${a.jefe_de_area}`
                  : a.persona_id ? `persona:${a.persona_id}` : `usuario:${a.usuario_id}`)))
                return (
                  <tr key={f.codigo} className="border-b border-gray-50 last:border-0 align-top">
                    <td className="px-5 py-3">
                      <button type="button" onClick={() => setViendo(f)} title={t('ot.permisos.personas.verHint')}
                        className="text-left group">
                        <span className="block font-medium text-gray-900 first-letter:uppercase group-hover:underline">{t(`ot.funciones.${f.codigo}`, { defaultValue: f.codigo })} <span className="text-gray-400 font-normal">· {t('ot.permisos.personas.ver')}</span></span>
                        <span className="block text-xs text-gray-500">{f.descripcion}</span>
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {f.asignaciones.map(a => (
                          <span key={a.id} title={a.nota || undefined}
                            className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 py-0.5 text-xs ${colorAsignacion(a)}`}>
                            {nombreAsignacion(a)}
                            <button type="button" onClick={() => revisar(a)} title={t('ot.permisos.revision')}
                              className={`px-1 rounded text-[10px] ${a.fecha_revision ? 'bg-amber-200/70 text-amber-900' : 'text-current opacity-40 hover:opacity-80'}`}>
                              {a.fecha_revision ? t('ot.permisos.revisar', { fecha: formatFecha(a.fecha_revision) }) : '⏱'}
                            </button>
                            <button type="button" onClick={() => quitar(f, a)} title={t('ot.permisos.quitar')}
                              className="w-4 h-4 rounded-full hover:bg-black/10 leading-none">×</button>
                          </span>
                        ))}
                        {!f.asignaciones.length && <span className="text-xs text-rose-600">{t('ot.permisos.nadie')}</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <AsignarA placeholder={t('ot.permisos.agregar')} vacio={t('ot.permisos.sinCoincidencias')}
                        onPick={v => agregar(f.codigo, v)}
                        groups={[
                          { label: t('ot.permisos.areas'), options: portadores.areas.filter(x => !ya.has(`area:${x.code}`)).map(x => ({ value: `area:${x.code}`, label: t('ot.permisos.personasDe', { area: x.name }) })) },
                          { label: t('ot.permisos.cargos'), options: portadores.cargos.filter(c => !ya.has(`cargo:${c}`)).map(c => ({ value: `cargo:${c}`, label: c })) },
                          { label: t('ot.permisos.jefes'), options: portadores.areas.filter(x => !ya.has(`jefe:${x.code}`)).map(x => ({ value: `jefe:${x.code}`, label: t('ot.permisos.jefeDe', { area: x.name }) })) },
                          { label: t('ot.permisos.personasRegistro'), options: portadores.personas.filter(p => !ya.has(`persona:${p.id}`)).map(p => ({ value: `persona:${p.id}`, label: p.nombre })) },
                          { label: t('ot.permisos.personas'), options: usuarios.filter(u => !ya.has(`usuario:${u.id}`)).map(u => ({ value: `usuario:${u.id}`, label: `${u.display_name}${u.email ? ` (${u.email})` : ''}` })) },
                        ]} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-3">{t('ot.permisos.nota')}</p>
      <Asignables />
      {viendo && (
        <PersonasConFuncion funcion={viendo.codigo} titulo={t(`ot.funciones.${viendo.codigo}`, { defaultValue: viendo.codigo })}
          descripcion={viendo.descripcion} onClose={() => setViendo(null)} t={t} />
      )}
    </div>
  )
}
